import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { ShieldCheck, LogOut, RefreshCw, Send, Copy, Wand2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fmtINR } from "@/lib/api";

export default function SuperAdmin() {
  const nav = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("sa_token"));
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [firms, setFirms] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetDlg, setResetDlg] = useState(null); // { firm, accountants, kind, username, password }

  const genPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
    let pw = "";
    for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    return pw + "@" + Math.floor(10 + Math.random() * 89);
  };

  const openResetDlg = async (firm) => {
    try {
      const { data } = await api.get(`/super-admin/firms/${firm._id_str}/details`);
      setResetDlg({
        firm,
        details: data,
        kind: "firm",
        username: data.accountants?.[0]?.username || "",
        password: genPassword(),
      });
    } catch (e) { toast.error("Failed to load firm details"); }
  };

  const submitResetAndSend = async ({ sendWhatsApp }) => {
    if (!resetDlg) return;
    const { firm, details, kind, username, password } = resetDlg;
    if (!password || password.length < 6) return toast.error("Password too short");
    if (kind === "accountant" && !username) return toast.error("Pick an accountant");
    try {
      await api.post(`/super-admin/firms/${firm._id_str}/reset-password`, {
        kind, new_password: password, username: kind === "accountant" ? username : null,
      });
      toast.success("Password reset");
      if (sendWhatsApp) {
        const who = kind === "firm" ? `Firm Login (${details.login_id})`
                  : kind === "owner" ? `Owner Login (${details.owner_username})`
                  : `Accountant Login (${username})`;
        const msg =
`Hello ${details.firm_name},

Your AccountifyPro *${who}* password has been reset by the administrator.

Firm ID: ${details.login_id}
Username: ${kind === "firm" ? details.login_id : kind === "owner" ? details.owner_username : username}
New Password: ${password}

Please login at https://accountifypro.co.in and change it from Settings.

- ANV ASSOCIATE (Helpline 8446463026)`;
        const phone = (details.contact || "").replace(/[^0-9]/g, "");
        const num = phone.startsWith("91") || phone.length !== 10 ? phone : "91" + phone;
        window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
      }
      setResetDlg(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyPw = () => {
    if (!resetDlg) return;
    navigator.clipboard.writeText(resetDlg.password);
    toast.success("Password copied");
  };

  const login = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/super-admin/login", creds);
      localStorage.setItem("sa_token", data.token);
      localStorage.setItem("role", "super_admin");
      setToken(data.token);
      toast.success("Super admin authenticated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  const load = async () => {
    try {
      const [f, s] = await Promise.all([
        api.get(`/super-admin/firms${filter ? `?status=${filter}` : ""}`),
        api.get("/super-admin/stats"),
      ]);
      setFirms(f.data); setStats(s.data);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token, filter]);

  const act = async (id, action) => {
    try {
      const { data } = await api.post(`/super-admin/firms/${id}/${action}`);
      toast.success(`Firm ${action}d`);
      // On approve, auto-open a WhatsApp draft to the firm's contact so the SA
      // can send an approval notice with one click.
      if (action === "approve" && data?.whatsapp_url) {
        window.open(data.whatsapp_url, "_blank");
        if (data.email_sent_to) {
          toast.success(`Approval email sent to ${data.email_sent_to}`);
        }
      }
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const logout = () => { localStorage.removeItem("sa_token"); localStorage.removeItem("role"); setToken(null); };

  if (!token) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-[#171717] border border-white/10 rounded-lg p-8">
        <ShieldCheck className="text-emerald-400 mb-2" />
        <div className="overline text-white/60">Super Admin</div>
        <h1 className="text-3xl font-bold tracking-tight">ANV_VIKRAM Console</h1>
        <p className="text-sm text-white/60 mt-1">Restricted access. AccountifyPro root operator.</p>
        <div className="mt-6 space-y-4">
          <div>
            <Label className="text-white/80">Username</Label>
            <Input className="bg-[#0A0A0A] border-white/10 text-white" value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} data-testid="sa-username" />
          </div>
          <div>
            <Label className="text-white/80">Password</Label>
            <Input type="password" className="bg-[#0A0A0A] border-white/10 text-white" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} data-testid="sa-password" />
          </div>
          <Button className="w-full btn-primary" disabled={busy} onClick={login} data-testid="sa-login">
            {busy ? "..." : "Sign in"}
          </Button>
          <button className="text-xs text-white/50 underline" onClick={() => nav("/")}>← Home</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 px-6 py-4 flex items-center gap-3">
        <ShieldCheck className="text-emerald-400" />
        <div>
          <div className="text-xs overline text-white/60">Super Admin</div>
          <div className="text-lg font-bold">ANV_VIKRAM Console</div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Button variant="outline" className="border-white/20 text-white bg-transparent hover:bg-white/10" onClick={load}><RefreshCw size={14} /></Button>
          <Button variant="outline" className="border-white/20 text-white bg-transparent hover:bg-white/10" onClick={logout} data-testid="sa-logout"><LogOut size={14} /> Logout</Button>
        </div>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "pending", label: "Pending", color: "text-amber-400" },
            { k: "active", label: "Active", color: "text-emerald-400" },
            { k: "suspended", label: "Suspended", color: "text-rose-400" },
            { k: "rejected", label: "Rejected", color: "text-white/50" },
          ].map((s) => (
            <div key={s.k} className="bg-[#171717] border border-white/10 rounded-lg p-4 kpi-card">
              <div className="overline text-white/50">{s.label} Firms</div>
              <div className={`text-3xl font-mono font-bold mt-1 ${s.color}`}>{stats[s.k] ?? 0}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-[#171717] border border-white/10 rounded-lg">
          <div className="p-4 flex items-center gap-3 border-b border-white/10">
            <div className="text-sm font-semibold">Firms Registry</div>
            <div className="ml-auto flex items-center gap-2">
              {["", "pending", "active", "suspended", "rejected"].map((s) => (
                <button key={s || "all"} onClick={() => setFilter(s)}
                  data-testid={`sa-filter-${s || "all"}`}
                  className={`text-xs px-3 py-1 rounded-full border ${filter === s ? "border-emerald-400 text-emerald-400" : "border-white/20 text-white/60 hover:text-white"}`}>
                  {s || "all"}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/60 text-xs uppercase tracking-wider">
                <tr><th className="text-left p-3">Firm</th><th className="text-left p-3">Login ID</th><th className="text-left p-3">Owner</th><th className="text-left p-3">Contact</th><th className="text-left p-3">GSTIN</th><th className="text-left p-3">Plan</th><th className="text-left p-3">Status</th><th className="text-left p-3">Actions</th></tr>
              </thead>
              <tbody>
                {firms.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-white/40">No firms</td></tr>}
                {firms.map((f) => (
                  <tr key={f._id_str} className="border-t border-white/10" data-testid={`sa-firm-row-${f._id_str}`}>
                    <td className="p-3 font-semibold">{f.firm_name}</td>
                    <td className="p-3 font-mono">{f.login_id}</td>
                    <td className="p-3">{f.owner_username}</td>
                    <td className="p-3">{f.contact}</td>
                    <td className="p-3 font-mono text-xs">{f.gstin || "—"}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        <select
                          value={f.plan || "professional"}
                          onChange={async (e) => {
                            const newPlan = e.target.value;
                            const months = Number(window.prompt("Months to activate for (12 = 1 year):", "12") || "12");
                            const trial = window.confirm("Mark as trial? (OK = trial · Cancel = paid)");
                            try {
                              await api.patch(`/super-admin/firms/${f._id_str}/plan`, { plan: newPlan, months, trial });
                              toast.success(`Plan updated to ${newPlan.toUpperCase()}${trial ? " (trial)" : ""}`);
                              load();
                            } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                          }}
                          data-testid={`sa-plan-${f._id_str}`}
                          className="bg-[#0A0A0A] border border-white/10 rounded-md px-2 py-1 text-xs text-white"
                        >
                          <option value="basic">BASIC ₹1,499</option>
                          <option value="professional">PRO ₹2,499</option>
                          <option value="business">BUSINESS ₹3,999</option>
                        </select>
                        {f.trial && (
                          <span className="chip chip-amber text-[9px]">TRIAL till {f.trial_end_at ? new Date(f.trial_end_at).toLocaleDateString() : "—"}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`chip ${f.status === "active" ? "chip-green" : f.status === "pending" ? "chip-amber" : f.status === "suspended" ? "chip-red" : "chip-slate"}`}>{f.status}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {f.status !== "active" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7" onClick={() => act(f._id_str, "approve")} data-testid={`sa-approve-${f._id_str}`}>Approve</Button>}
                        {f.status !== "rejected" && <Button size="sm" variant="outline" className="border-white/20 h-7 text-white bg-transparent hover:bg-white/10" onClick={() => act(f._id_str, "reject")} data-testid={`sa-reject-${f._id_str}`}>Reject</Button>}
                        {f.status === "active" && <Button size="sm" variant="outline" className="border-rose-500/40 h-7 text-rose-400 bg-transparent hover:bg-rose-500/10" onClick={() => act(f._id_str, "suspend")} data-testid={`sa-suspend-${f._id_str}`}>Suspend</Button>}
                        {f.status === "suspended" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7" onClick={() => act(f._id_str, "activate")} data-testid={`sa-activate-${f._id_str}`}>Activate</Button>}
                        <Button size="sm" variant="outline" className="border-white/20 h-7 text-white bg-transparent hover:bg-white/10" onClick={async () => {
                          const { data } = await api.get(`/super-admin/firms/${f._id_str}/details`);
                          const accStr = data.accountants.map(a => a.username).join(", ") || "—";
                          const msg = `Firm: ${data.firm_name}\nLogin ID: ${data.login_id}\nOwner username: ${data.owner_username}\nAccountants: ${accStr}\nEdit PIN: ${data.edit_pin}\nContact: ${data.contact || "—"}\n\nPasswords are hashed and cannot be shown — use the Reset buttons to set new ones.`;
                          alert(msg);
                        }} data-testid={`sa-details-${f._id_str}`}>Details</Button>
                        <Button size="sm" variant="outline" className="border-amber-500/40 h-7 text-amber-300 bg-transparent hover:bg-amber-500/10" onClick={() => openResetDlg(f)} data-testid={`sa-reset-${f._id_str}`}>
                          <Wand2 size={12} className="mr-1" /> Reset & Send
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Reset & WhatsApp dialog */}
      <Dialog open={!!resetDlg} onOpenChange={(v) => !v && setResetDlg(null)}>
        <DialogContent className="max-w-lg bg-[#171717] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Reset Password & Send via WhatsApp</DialogTitle>
          </DialogHeader>
          {resetDlg && (
            <div className="space-y-4">
              <div className="bg-black/30 rounded-md p-3 text-sm">
                <div className="font-semibold text-emerald-300">{resetDlg.details.firm_name}</div>
                <div className="text-white/60 text-xs mt-1">
                  Contact: <span className="font-mono">{resetDlg.details.contact || "—"}</span>
                </div>
              </div>
              <div>
                <Label className="text-white/80 text-xs">Reset which credential?</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {["firm", "owner", "accountant"].map((k) => (
                    <button
                      key={k}
                      onClick={() => setResetDlg({ ...resetDlg, kind: k })}
                      data-testid={`sa-reset-kind-${k}`}
                      className={`text-xs px-3 py-2 rounded-md border capitalize ${
                        resetDlg.kind === k
                          ? "border-emerald-400 text-emerald-300 bg-emerald-500/10"
                          : "border-white/20 text-white/60"
                      }`}
                    >
                      {k}
                    </button>
                  ))}
                </div>
              </div>
              {resetDlg.kind === "accountant" && (
                <div>
                  <Label className="text-white/80 text-xs">Accountant username</Label>
                  <select
                    value={resetDlg.username}
                    onChange={(e) => setResetDlg({ ...resetDlg, username: e.target.value })}
                    className="w-full h-9 mt-1 rounded-md bg-[#0A0A0A] border border-white/10 text-white px-2 text-sm"
                    data-testid="sa-reset-acc-select"
                  >
                    {(resetDlg.details.accountants || []).map((a) => (
                      <option key={a.username} value={a.username}>{a.username}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <Label className="text-white/80 text-xs">New password</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    className="bg-[#0A0A0A] border-white/10 text-white font-mono"
                    value={resetDlg.password}
                    onChange={(e) => setResetDlg({ ...resetDlg, password: e.target.value })}
                    data-testid="sa-reset-pw"
                  />
                  <Button variant="outline" onClick={() => setResetDlg({ ...resetDlg, password: genPassword() })} className="border-white/20 text-white bg-transparent hover:bg-white/10" title="Regenerate">
                    <RefreshCw size={14} />
                  </Button>
                  <Button variant="outline" onClick={copyPw} className="border-white/20 text-white bg-transparent hover:bg-white/10" title="Copy">
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setResetDlg(null)} className="border-white/20 text-white bg-transparent hover:bg-white/10">Cancel</Button>
                <Button variant="outline" onClick={() => submitResetAndSend({ sendWhatsApp: false })} className="border-white/20 text-white bg-transparent hover:bg-white/10" data-testid="sa-reset-only">
                  Reset only
                </Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => submitResetAndSend({ sendWhatsApp: true })} data-testid="sa-reset-and-send">
                  <Send size={14} className="mr-1" /> Reset & Send WhatsApp
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
